import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  Input,
  TextField,
  Typography,
} from "@mui/material";
import { ChangeEvent, FormEvent, useState } from "react";
import { CustomEvent, Map } from "schemas/functions";

import Alert from "components/Alert";
import StyledPage from "components/Styled/Page";
import { fetcherAuth } from "utils/api";
import { useAlert } from "utils/useAlert";

const AdminScraping = () => {
  const { getToken } = useAuth();
  const [message, severity, setAlert, closeAlert] = useAlert();

  const [loading, setLoading] = useState(false);

  const [file, setFile] = useState<File | null>(null);
  const [location, setLocation] = useState<string>("");
  const [keywords, setKeywords] = useState<string>("");

  const [pageParams, setPageParams] = useState<Map>({
    per_page: 25,
    stopped_at: 100,
    page: 1,
  });

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    setFile(selectedFile || null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    try {
      const data = new FormData();
      if (!file) {
        throw new Error("You must upload a CSV file");
      }

      const fileExt = file.name.split(".").pop()?.toLowerCase();
      if (fileExt !== "csv") {
        throw new Error("You must select a CSV file");
      }
      data.append("file", file);

      await fetcherAuth(
        getToken,
        `/api/admin/scraping/social-media-accounts-csv`,
        "POST",
        {},
        data,
        false,
        true,
      );
      setAlert("Uploaded accounts", "success");
    } catch (error) {
      setAlert(error?.message, "warning");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitApolloScrape = async (
    event: FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    const data: Map = {
      location,
      keyword_tags: keywords?.split(", "),
    };
    if (pageParams?.page && pageParams?.page > 0) {
      data["page"] = pageParams.page;
    }
    if (pageParams?.stopped_at && pageParams?.stopped_at > pageParams?.page) {
      data["stopped_at"] = pageParams.stopped_at;
    }
    if (pageParams?.per_page && pageParams?.per_page > 0) {
      data["per_page"] = pageParams?.per_page;
    }

    try {
      setLoading(true);
      await fetcherAuth(
        getToken,
        `/api/admin/apollo-organizations/find-brands`,
        "POST",
        {},
        data,
      );
      setAlert("Successfully started scraping", "success");
    } catch (error) {
      setAlert(error?.message, "warning");
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledPage
      content={
        <Grid sx={{ mt: 2 }}>
          <Typography variant="h3" gutterBottom>
            <strong>Scraping</strong>
          </Typography>

          <Typography variant="h5">Upload accounts to Scrape</Typography>
          <form onSubmit={handleSubmit}>
            <FormHelperText sx={{ pb: 2 }}>
              This will add all these accounts (remove duplicates) to our
              database and enqueue them to scrape on a daily basis.
            </FormHelperText>
            <FormControl>
              <Input type="file" sx={{ mr: 1 }} onChange={handleFileChange} />
            </FormControl>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
            >
              Submit
            </Button>
          </form>

          <Box sx={{ my: 4 }}>
            <Typography variant="h5">Apollo Find Organizations</Typography>
            <FormHelperText sx={{ pb: 2 }}>
              Enter search bar below to scrape Apollo Brands.
            </FormHelperText>
            <form onSubmit={handleSubmitApolloScrape}>
              <FormControl fullWidth>
                <Grid container gap={2} item xs={6} alignItems="center">
                  <Grid item xs={3}>
                    <FormHelperText sx={{ ml: 0 }}>Location</FormHelperText>
                    <TextField
                      fullWidth
                      placeholder="Australia, UK, Europe..."
                      onChange={(e: CustomEvent) => setLocation(e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <FormHelperText sx={{ ml: 0 }}>
                      Keywords (comma-separated)
                    </FormHelperText>
                    <TextField
                      fullWidth
                      placeholder="supplements, nutritions, vitamins..."
                      onChange={(e: CustomEvent) => setKeywords(e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <FormHelperText sx={{ ml: 0 }}>
                      Page (optional)
                    </FormHelperText>
                    <TextField
                      fullWidth
                      type="number"
                      value={pageParams.page || ""}
                      onChange={(e: CustomEvent) =>
                        setPageParams({
                          ...pageParams,
                          page: parseInt(e.target.value, 10),
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <FormHelperText sx={{ ml: 0 }}>
                      Per Page (optional)
                    </FormHelperText>
                    <TextField
                      fullWidth
                      type="number"
                      value={pageParams.per_page}
                      onChange={(e: CustomEvent) =>
                        setPageParams({
                          ...pageParams,
                          per_page: parseInt(e.target.value, 10),
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <FormHelperText sx={{ ml: 0 }}>
                      Stopped At (optional)
                    </FormHelperText>
                    <TextField
                      fullWidth
                      type="number"
                      value={pageParams.stopped_at}
                      onChange={(e: CustomEvent) =>
                        setPageParams({
                          ...pageParams,
                          stopped_at: parseInt(e.target.value, 10),
                        })
                      }
                    />
                  </Grid>

                  <LoadingButton
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                    disabled={!(location || keywords)}
                    loading={loading}
                  >
                    Submit
                  </LoadingButton>
                </Grid>
              </FormControl>
            </form>
          </Box>
          <Alert
            message={message}
            severity={severity}
            closeAlert={closeAlert}
          />
        </Grid>
      }
    />
  );
};

export default AdminScraping;
